import { useEffect, useRef, useState } from "react";
import { Alert, Progress, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { useSignalR } from "../context/SignalRContext";

/**
 * Sunucudaki OperationProgressStatus ile birebir.
 */
export const OperationStatus = {
    RUNNING: 'running',
    COMPLETED: 'completed',
    FAILED: 'failed',
}

/**
 * Uzun süren bir işlemin (çekim birleştirme, hasta güncelleme) canlı ilerlemesi.
 *
 * Kimliği İSTEMCİ üretir ve isteğin gövdesinde gönderir; sunucu ilerlemeyi aynı kimlikle yayınlar.
 * Böylece aynı kullanıcının başka sekmede başlattığı işlem bu paneli oynatmaz.
 *
 * `start()` her çağrıldığında yeni bir kimlik üretilir — aynı modalda ikinci kez denendiğinde
 * önceki işlemin geç gelen olayları sayacı geri saydırmasın.
 */
export function useOperationProgress() {
    const { onOperationProgress } = useSignalR();
    const [operationId, setOperationId] = useState(null);
    const [progress, setProgress] = useState(null);

    // Olay dinleyicisi effect'i her ilerlemede yeniden kurulmasın diye kimlik ref'te de tutulur.
    const operationIdRef = useRef(null);

    const start = () => {
        const id = v4();
        operationIdRef.current = id;
        setOperationId(id);
        setProgress({ current: 0, total: 0, status: OperationStatus.RUNNING, message: null });
        return id;
    }

    const reset = () => {
        operationIdRef.current = null;
        setOperationId(null);
        setProgress(null);
    }

    /** İstek hata ile döndüyse: sunucudan ilerleme gelmemiş olabilir, paneli biz kapatırız. */
    const fail = (error) => {
        setProgress(prev => ({ ...(prev || { current: 0, total: 0 }), status: OperationStatus.FAILED, error }));
    }

    useEffect(() => {
        const payload = onOperationProgress?.payload;

        if (!payload || !operationIdRef.current || payload.operationId !== operationIdRef.current)
            return;

        setProgress({
            current: payload.current,
            total: payload.total,
            status: payload.status,
            message: payload.message,
            error: payload.error,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onOperationProgress?.key]);

    return { operationId, progress, start, reset, fail, running: !!operationId };
}

/**
 * İlerleme paneli. Toplam adım sayısı sunucudan gelene kadar belirsiz (indeterminate) görünür:
 * ilk olay gelmeden önce kaç çekim işleneceğini istemci bilmiyor.
 */
export default function OperationProgress({ progress, title }) {
    const { t } = useTranslation();

    if (!progress)
        return null;

    const { current = 0, total = 0, status, message, error } = progress;
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;

    if (status === OperationStatus.FAILED) {
        return (
            <Alert
                type={'error'}
                showIcon
                message={t('Operation failed')}
                description={(
                    <>
                        <div>{error}</div>
                        <div>{t('Completed steps were rolled back in RIS.')}</div>
                    </>
                )}
            />
        );
    }

    return (
        <Space direction={'vertical'} style={{ width: '100%' }}>
            <Typography.Text strong>{title}</Typography.Text>
            <Progress
                percent={percent}
                status={status === OperationStatus.COMPLETED ? 'success' : 'active'}
                format={() => (total > 0 ? `${current}/${total}` : '')}
            />
            <Typography.Text type={'secondary'}>
                {status === OperationStatus.COMPLETED
                    ? t('Completed')
                    : (message || t('Starting...'))}
            </Typography.Text>
            {status !== OperationStatus.COMPLETED && (
                <Typography.Text type={'warning'}>
                    {t('Please do not close this window until the operation finishes.')}
                </Typography.Text>
            )}
        </Space>
    );
}
